import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { LocaleService } from '@next/core-lib/i18n';
import { NotificationActions as UiNotificationActions } from '@next/core-lib/notification';
import { ofType, Actions, createEffect } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';

import * as NotificationActions from './notification.actions';
import { NotificationService } from '../services/notification.service';

@Injectable()
export class NotificationEffects {
  loadNotifications$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationActions.loadNotificationsRequest),
      switchMap(() =>
        this.api.getNotifications().pipe(
          map((notifications) => {
            notifications.sort((a, b) => new Date(b.start).getTime() - new Date(a.start).getTime());
            return NotificationActions.loadNotificationsSuccess({ notifications });
          }),
          catchError(() => {
            const errorMessage = this.localeService.translate('user-notifications.form.load-error');
            return of(UiNotificationActions.errorSnackbar({ message: errorMessage }), NotificationActions.loadNotificationsFailure());
          }),
        ),
      ),
    ),
  );

  upsertNotification$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationActions.upsertNotificationRequest),
      switchMap((request) => {
        return this.api.upsertNotification(request.notification).pipe(
          map((notification) =>
            NotificationActions.upsertNotificationSuccess({
              notification: {
                ...notification,
              },
            }),
          ),
          catchError(() => {
            const errorMessage = this.localeService.translate('user-notifications.form.save-error');
            return of(UiNotificationActions.errorSnackbar({ message: errorMessage }), NotificationActions.upsertNotificationFailure({ error: errorMessage }));
          }),
        );
      }),
    ),
  );

  upsertNotificationSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(NotificationActions.upsertNotificationSuccess),
        tap(() => {
          this.router.navigate(['/user-notifications']);
        }),
      ),
    { dispatch: false },
  );

  softDeleteNotificationSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(NotificationActions.softDeleteNotificationSuccess),
        tap(() => {
          // Navigate back to list when deletion is successful
          if (this.router.url.includes('/user-notifications/') && this.router.url !== '/user-notifications') {
            this.router.navigate(['/user-notifications']);
          }
        }),
      ),
    { dispatch: false },
  );

  softDeleteNotification$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationActions.softDeleteNotificationRequest),
      switchMap((action) =>
        this.api.softDeleteNotification(action.id).pipe(
          switchMap(() => {
            const successMessage = this.localeService.translate('user-notifications.delete-success');
            return [NotificationActions.softDeleteNotificationSuccess({ id: action.id }), UiNotificationActions.successSnackbar({ message: successMessage })];
          }),
          catchError(() => {
            const errorMessage = this.localeService.translate('user-notifications.delete-error');
            return [UiNotificationActions.errorSnackbar({ message: errorMessage }), NotificationActions.softDeleteNotificationFailure({ error: errorMessage })];
          }),
        ),
      ),
    ),
  );

  constructor(
    private readonly actions$: Actions,
    private readonly api: NotificationService,
    private readonly router: Router,
    private readonly localeService: LocaleService,
  ) {}
}
